import { useCallback } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { useAuthStore } from '@/store/authStore';
import { extractAdminGateRole, getStoredToken, linkRail1 } from '@/lib/api';

/**
 * Attempts (or retries) linking the current rail1 account to bigwater_backend.
 * Never throws — failure is surfaced via `rail1LinkStatus` in the auth store
 * so callers (post-login and the retry badge) don't need their own try/catch.
 * A failure caused by an admin/superadmin account (no participant profile)
 * additionally sets `adminGateRole`, which `AdminGateOverlay` reads.
 */
export function useRail1Link(): () => Promise<void> {
  const setRail1LinkStatus = useAuthStore((s) => s.setRail1LinkStatus);
  const setAdminGateRole = useAuthStore((s) => s.setAdminGateRole);
  const { getAccessToken } = usePrivy();

  return useCallback(async () => {
    setRail1LinkStatus('pending');
    try {
      const rail1Token = getStoredToken();
      if (!rail1Token) throw new Error('missing rail1 token');
      const privyToken = await getAccessToken();
      if (!privyToken) throw new Error('No Privy session');
      await linkRail1(privyToken);
      setRail1LinkStatus('linked');
    } catch (err) {
      const adminRole = extractAdminGateRole(err);
      if (adminRole) setAdminGateRole(adminRole);
      setRail1LinkStatus('failed');
    }
  }, [setRail1LinkStatus, setAdminGateRole, getAccessToken]);
}
